/**
 * Beta Access Protection Utilities
 * Manages beta access code validation
 */

import { getStoredBetaCode } from "@/lib/storage";

/**
 * Check if the environment requires beta access protection
 * Local development (localhost) is exempted
 */
export function requiresBetaAccess(): boolean {
  // Exempt localhost/local development
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return false;
    }
  }

  // Check environment variable - only production is not protected
  const appEnv = process.env.NEXT_PUBLIC_APP_ENV;
  return appEnv !== "production";
}

/**
 * Validate beta access code against server-side environment variable
 * Makes an API call to validate the code securely on the server
 */
export async function validateBetaCode(code: string): Promise<boolean> {
  try {
    const response = await fetch("/api/beta-access/validate", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ code }),
    });

    const data = await response.json();
    return data.valid === true;
  } catch (error) {
    console.error("Failed to validate beta code:", error);
    return false;
  }
}

/**
 * Check if user has valid beta access
 * Returns true if access is granted, false if access should be blocked
 */
export async function hasValidBetaAccess(): Promise<boolean> {
  // If beta access is not required, grant access
  if (!requiresBetaAccess()) {
    return true;
  }

  // Check stored code
  const storedCode = getStoredBetaCode();
  if (!storedCode) {
    return false;
  }

  // Validate stored code
  return await validateBetaCode(storedCode);
}
